/**
 * 文件路径: D:\XXY\MeiShan_Project\frontend\shared\api\http.ts
 * HTTP客户端配置，包含请求/响应拦截器
 */

import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosError } from 'axios'
import { unref } from 'vue'
import type { ApiResponse, ErrorResponse } from '../types'
import { ApiErrorCode } from '../types'

// 统一日志开关：仅当 VITE_SDK_LOG === 'true' 时输出调试日志
const SDK_LOG_ENABLED: boolean = String(import.meta.env?.VITE_SDK_LOG ?? 'false') === 'true'

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function toRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {}
}

/**
 * 规范化 API 路径，确保 RESTful 端点带尾斜杠，避免后端对无尾斜杠路径返回 401（ECS 环境已验证）。
 *
 * 输入：相对路径（如 'employees?page=1' 或 'enterprises/151/departments/tree'）
 * 输出：带尾斜杠的相对路径（如 'employees/?page=1' 或 'enterprises/151/departments/tree/'）
 */
function normalizeApiUrl(url: string | undefined | null): string | undefined {
  if (url == null) return undefined
  if (url === '') return undefined
  let raw = String(url)
  // 绝对 URL 或 data: 等协议不处理
  if (/^(https?:)?\/\//i.test(raw) || raw.startsWith('data:')) return raw

  // ⭐ 稳定机制：纠偏“错误的相对写法”
  // 常见踩坑：
  // - getAxiosInstance().get('api/v1/xxx/')（少了前导 /）→ /api/v1/api/v1/xxx/
  // - getAxiosInstance().get('/api/v1/xxx/')（带了前导 /）→ /api/v1/api/v1/xxx/（Axios 仍会拼 baseURL）
  // 这里统一剥离掉前置的 /api/v1/、api/v1/、/api/、api/，让它回到正确的相对路径 xxx/
  const hadLeadingSlash = raw.startsWith('/')
  raw = hadLeadingSlash ? raw.slice(1) : raw
  const lower = raw.toLowerCase()
  const stripPrefix = (prefix: string) => {
    raw = raw.slice(prefix.length)
    if (SDK_LOG_ENABLED) {
      try { console.warn('[HTTP][normalizeApiUrl] stripped wrong prefix:', prefix, '->', raw) } catch {}
    }
  }
  if (lower.startsWith('api/v1/')) stripPrefix(raw.slice(0, 'api/v1/'.length))
  else if (lower === 'api/v1') stripPrefix(raw.slice(0, 'api/v1'.length))
  else if (lower.startsWith('api/')) stripPrefix(raw.slice(0, 'api/'.length))
  else if (lower === 'api') stripPrefix(raw.slice(0, 'api'.length))
  // 防御：剥离后若变为空，则保持原样（避免请求到 baseURL 根）
  if (!raw) return url ?? undefined

  // 统一去掉可能残留的前导 '/'，避免拼接出双斜杠
  const trimmed = raw.startsWith('/') ? raw.slice(1) : raw

  // 若含查询串，把 '/' 补到 '?' 之前：employees?page=1 -> employees/?page=1
  const qIndex = trimmed.indexOf('?')
  const base = qIndex >= 0 ? trimmed.slice(0, qIndex) : trimmed
  const query = qIndex >= 0 ? trimmed.slice(qIndex) : ''

  // 已带尾斜杠则不处理
  if (base.endsWith('/')) return `${base}${query}`

  return `${base}/${query}`
}

// Token管理类（支持按应用前缀隔离存储，确保多个后台可同时登录互不影响）
class TokenManager {
  // 允许通过环境变量或 URL 基础路径自动推断存储前缀
  private static keyPrefix: string | null = null

  private static resolvePrefix(): string {
    // 优先读取构建期注入的环境变量
    const envPrefix = import.meta.env?.VITE_APP_STORAGE_PREFIX
    if (envPrefix && typeof envPrefix === 'string') {
      return envPrefix
    }
    // 其次根据路径自动推断（dev/prod 通用）
    const path = typeof window !== 'undefined' ? window.location.pathname : ''
    if (path.includes('/operator-admin/')) return 'operator_'
    if (path.includes('/merchant-admin/')) return 'merchant_'
    if (path.includes('/enterprise-admin/')) return 'enterprise_'
    return ''
  }

  static setKeyPrefix(prefix: string) {
    if (SDK_LOG_ENABLED) console.info('[TokenManager] setKeyPrefix:', prefix)
    this.keyPrefix = prefix || ''
  }

  static getKeyPrefix(): string {
    if (this.keyPrefix === null) {
      this.keyPrefix = this.resolvePrefix()
    }
    if (SDK_LOG_ENABLED) { try { console.info('[TokenManager] getKeyPrefix ->', this.keyPrefix) } catch {} }
    return this.keyPrefix
  }

  private static accessTokenKey(): string {
    return `${this.getKeyPrefix()}access_token`
  }

  private static refreshTokenKey(): string {
    return `${this.getKeyPrefix()}refresh_token`
  }

  static getAccessToken(): string | null {
    const v = localStorage.getItem(this.accessTokenKey())
    if (SDK_LOG_ENABLED) { try { console.info('[TokenManager] getAccessToken exists:', Boolean(v)) } catch {} }
    return v
  }

  static getRefreshToken(): string | null {
    const v = localStorage.getItem(this.refreshTokenKey())
    if (SDK_LOG_ENABLED) { try { console.info('[TokenManager] getRefreshToken exists:', Boolean(v)) } catch {} }
    return v
  }

  static setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(this.accessTokenKey(), accessToken)
    localStorage.setItem(this.refreshTokenKey(), refreshToken)
    if (SDK_LOG_ENABLED) { try { console.info('[TokenManager] setTokens done') } catch {} }
  }
  
  static setAccessToken(accessToken: string): void {
    localStorage.setItem(this.accessTokenKey(), accessToken)
    if (SDK_LOG_ENABLED) { try { console.info('[TokenManager] setAccessToken done') } catch {} }
  }

  static clearTokens(): void {
    localStorage.removeItem(this.accessTokenKey())
    localStorage.removeItem(this.refreshTokenKey())
    if (SDK_LOG_ENABLED) { try { console.info('[TokenManager] clearTokens done') } catch {} }
  }

  static hasToken(): boolean {
    const has = !!this.getAccessToken()
    if (SDK_LOG_ENABLED) { try { console.info('[TokenManager] hasToken ->', has) } catch {} }
    return has
  }

  /**
   * 检查 access_token 是否已过期（解析 JWT exp claim）。
   * 若无 token 或解析失败，视为已过期。
   */
  static isAccessTokenExpired(): boolean {
    const token = this.getAccessToken()
    if (!token) return true
    try {
      const parts = token.split('.')
      if (parts.length !== 3) return true
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
      if (!payload?.exp) return true
      return Math.floor(Date.now() / 1000) >= payload.exp
    } catch {
      return true
    }
  }

  /**
   * 检查 refresh_token 是否已过期。
   */
  static isRefreshTokenExpired(): boolean {
    const token = this.getRefreshToken()
    if (!token) return true
    try {
      const parts = token.split('.')
      if (parts.length !== 3) return true
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
      if (!payload?.exp) return true
      return Math.floor(Date.now() / 1000) >= payload.exp
    } catch {
      return true
    }
  }

  /**
   * 检查是否有有效的认证凭证：
   * - access_token 未过期 → true
   * - access_token 过期但 refresh_token 未过期 → true（可续期）
   * - 两者均过期 → 清除 token 并返回 false
   */
  static hasValidAuth(): boolean {
    if (!this.getAccessToken()) return false
    if (!this.isAccessTokenExpired()) return true
    if (!this.isRefreshTokenExpired()) return true
    this.clearTokens()
    return false
  }
}

// HTTP客户端类
class HttpClient {
  private instance: AxiosInstance
  private isRefreshing = false
  /* eslint-disable no-unused-vars, @typescript-eslint/no-unused-vars */
  private failedQueue: Array<{
    resolve: (_token: string) => void
    reject: (_error: unknown) => void
  }> = []
  /* eslint-enable no-unused-vars, @typescript-eslint/no-unused-vars */
  private sdkLogEnabled: boolean
  private sdkLogSample: number

  constructor(config: AxiosRequestConfig = {}) {
    // 统一保证 baseURL 以斜杠结尾，便于相对路径正常拼接（e.g. 'constants/meal-segments/')
    const rawBase = import.meta.env?.VITE_API_BASE_URL || '/api/v1'
    // 同时保证：
    // 1) 绝对地址(http/https)原样保留
    // 2) 非绝对地址强制以 '/' 开头，避免被页面 base '/operator-admin/' 拼接成相对路径
    // 3) 统一以 '/' 结尾，便于路径拼接
    const rawStr = String(rawBase)
    const withPrefix = /^https?:\/\//i.test(rawStr) ? rawStr : (rawStr.startsWith('/') ? rawStr : `/${rawStr}`)
    const normalizedBase = withPrefix.endsWith('/') ? withPrefix : `${withPrefix}/`
    const defaultConfig: AxiosRequestConfig = {
      baseURL: normalizedBase,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    }

    // 允许外部注入覆盖项（如 timeout/headers），但保持默认 header 存在
    const mergedConfig: AxiosRequestConfig = {
      ...defaultConfig,
      ...config,
      headers: { ...(defaultConfig.headers || {}), ...(config.headers || {}) }
    }
    this.instance = axios.create(mergedConfig)
    // 采样日志开关（开发期调试用）
    const rawFlag = import.meta.env?.VITE_SDK_LOG ?? 'false'
    const rawSample = import.meta.env?.VITE_SDK_LOG_SAMPLE ?? '0.1'
    this.sdkLogEnabled = String(rawFlag) === 'true'
    const s = parseFloat(String(rawSample))
    this.sdkLogSample = !isNaN(s) && s >= 0 && s <= 1 ? s : 0.1
    this.setupInterceptors()
  }

  private setupInterceptors(): void {
    // 请求拦截器
    this.instance.interceptors.request.use(
      async (config) => {
        // ✅ 强制补齐尾斜杠，避免 DRF 端点无尾斜杠导致的 401（尤其是带 query 的列表请求）
        config.url = normalizeApiUrl(config.url)

        // ⭐ multipart 修复：
        // - axios 实例默认 headers 里设置了 application/json
        // - 如果 data 是 FormData 且 headers 仍带 Content-Type=application/json（或手动 multipart 无 boundary），后端会拿不到 file
        // - 因此：遇到 FormData 直接删除 Content-Type，让浏览器/axios 自动生成带 boundary 的 multipart
        try {
          const isFormData =
            typeof FormData !== 'undefined' &&
            !!config.data &&
            typeof config.data === 'object' &&
            (config.data as unknown) instanceof FormData
          if (isFormData) {
            this.removeContentTypeHeader(config.headers as unknown)
          }
        } catch {}

        const token = TokenManager.getAccessToken()
        if (SDK_LOG_ENABLED) { try { console.info('[HTTP][request] start', { url: config.url, hasToken: !!token }) } catch {} }
        if (token) {
          // 兼容 Axios v1 的 AxiosHeaders 对象
          const headers = config.headers as unknown
          const hasSetter = !!headers && typeof (headers as { set?: unknown }).set === 'function'
          if (hasSetter) {
            // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
            ;(headers as { set: (name: string, value: string) => void }).set('Authorization', `Bearer ${token}`)
          } else {
            // 退回到普通对象写法
            const merged: Record<string, string> = {}
            const raw = config.headers && typeof config.headers === 'object' ? (config.headers as Record<string, unknown>) : {}
            for (const [hk, hv] of Object.entries(raw)) {
              if (hv === undefined || hv === null) continue
              merged[hk] = String(hv)
            }
            merged.Authorization = `Bearer ${token}`
            config.headers = merged as unknown as typeof config.headers
          }
          if (SDK_LOG_ENABLED) {
            try {
              const h = config.headers as unknown
              const getFn =
                !!h && typeof (h as { get?: unknown }).get === 'function'
                  ? // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
                    (h as { get: (name: string) => unknown }).get
                  : null
              const authHeader = getFn ? getFn('Authorization') : (config.headers as Record<string, unknown> | undefined)?.Authorization
              console.log('[HTTP] Injected Authorization header for', config.url, Boolean(authHeader))
            } catch {}
          }
        }
        // 注入运营上下文（租户/范围/周期）
        try {
          const { useContextStore } = await import('../stores/context')
          const ctx = useContextStore()
          // 仅当存在租户上下文时才注入自定义头，避免跨域场景触发不必要的预检失败
          if (ctx?.activeOperatorId) {
            const headers = config.headers as unknown
            const hasSetter = !!headers && typeof (headers as { set?: unknown }).set === 'function'
            if (hasSetter) {
              // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
              ;(headers as { set: (name: string, value: string) => void }).set('X-Operator-Id', String(ctx.activeOperatorId))
            } else {
              const merged: Record<string, string> = {}
              const raw = config.headers && typeof config.headers === 'object' ? (config.headers as Record<string, unknown>) : {}
              for (const [hk, hv] of Object.entries(raw)) {
                if (hv === undefined || hv === null) continue
                merged[hk] = String(hv)
              }
              merged['X-Operator-Id'] = String(ctx.activeOperatorId)
              config.headers = merged as unknown as typeof config.headers
            }
          } else {
            // 保留一次性提示，但不注入自定义请求头
            const hintedKey = 'ms:operator-hint-shown'
            if (!sessionStorage.getItem(hintedKey)) {
              if (SDK_LOG_ENABLED) { try { console.warn('[Operator] 未选择运营商租户，将使用默认上下文或返回全局范围数据。') } catch {} }
              sessionStorage.setItem(hintedKey, '1')
            }
          }
          // 关键修复：清理 params 中的 undefined/null，避免被 URLSearchParams 序列化为字符串 "undefined"
          // 这会导致后端按 search=undefined 之类的值过滤，出现“接口明明有数据但列表为空”的假象。
          const rawParams: Record<string, unknown> =
            config.params && typeof config.params === 'object' ? (config.params as Record<string, unknown>) : {}
          const q = new URLSearchParams()
          if (rawParams && typeof rawParams === 'object') {
            for (const [k, v] of Object.entries(rawParams)) {
              if (v === undefined || v === null) continue
              if (Array.isArray(v)) {
                q.set(k, v.map((i) => String(i)).join(','))
              } else {
                q.set(k, String(v))
              }
            }
          }
          // Pinia setup stores normally expose unwrapped values, but consumers with a
          // separate Vue type instance can retain Ref<T> in the inferred store type.
          // unref() is safe for both representations and keeps shared HTTP portable.
          const selectedScopes = unref(ctx.selectedScopes)
          if (selectedScopes.departments?.length) q.set('departments', selectedScopes.departments.join(','))
          if (selectedScopes.cities?.length) q.set('cities', selectedScopes.cities.join(','))
          if (selectedScopes.regions?.length) q.set('regions', selectedScopes.regions.join(','))
          const urlPath = String(config.url || '')
          // 新策略：默认不注入；仅当 meta.attachPeriod=true 且路径在白名单时注入
          const allowList = [
            'calendar',
            'reports',
            'statistics',
            'readiness'
          ]
          const meta = (config as unknown as { meta?: unknown }).meta
          const wantsAttach = toRecord(meta).attachPeriod === true
          const pathAllowed = allowList.some((k) => urlPath.includes(k))
          if (wantsAttach && pathAllowed && ctx?.period) {
            q.set('period', String(ctx.period))
          }
          // 以 q 为准重建 params，确保不会把 undefined 重新带回去
          const merged = Object.fromEntries(q)
          config.params = merged
        } catch {}

        if (SDK_LOG_ENABLED) { try { console.info('[HTTP][request] ready', { url: config.url }) } catch {} }
        return config
      },
      (error) => Promise.reject(error)
    )

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response) => {
        if (this.sdkLogEnabled && Math.random() < this.sdkLogSample) {
          try {
            const cfg = toRecord(response.config as unknown)
            const metadata = toRecord(cfg.metadata)
            const startTime = metadata.startTime
            const startMs = startTime instanceof Date ? startTime.getTime() : Date.now()
            const ms = Date.now() - startMs
            console.info('[SDK][HTTP][ok]', response.status, response.config?.method?.toUpperCase(), response.config?.url, `${ms}ms`)
          } catch {}
        } else if (SDK_LOG_ENABLED) { try { console.info('[HTTP][response] ok', { url: response.config?.url, status: response.status }) } catch {} }
        return response
      },
      async (error: AxiosError) => {
        const originalRequest = (error.config || {}) as AxiosRequestConfig & {
          _retry?: boolean
          metadata?: { startTime?: Date }
        };

        // ⭐ 登录/刷新等鉴权端点不应触发“自动刷新 token”
        // 否则会出现：登录失败(401) → 尝试 refresh → 没有 refresh token → 抛出技术提示给用户
        const requestUrl = String(originalRequest.url || '').toLowerCase()
        const isAuthEndpoint =
          requestUrl.includes('/auth/login') ||
          requestUrl.includes('auth/login') ||
          requestUrl.includes('/auth/token/refresh') ||
          requestUrl.includes('auth/token/refresh')

        if (error.response?.status === 401 && isAuthEndpoint) {
          return Promise.reject(this.formatError(error))
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then(() => {
              // 关键修复：删除旧的认证头，让请求拦截器重新设置
              this.removeAuthorizationHeader(originalRequest.headers as unknown)
              return this.instance(originalRequest);
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const refreshToken = TokenManager.getRefreshToken();
            if (SDK_LOG_ENABLED) console.log('[Token Refresh] Starting refresh with token:', !!refreshToken);
            if (!refreshToken) {
              // 无 refresh token：说明当前会话无法续期，清理并让上层按 401 处理（给出友好提示）
              TokenManager.clearTokens()
              try {
                const event = new CustomEvent('auth-failure')
                window.dispatchEvent(event)
              } catch {}
              return Promise.reject(this.formatError(error))
            }

            // 使用独立的axios实例来刷新令牌，避免循环拦截
            const refreshUrl = `${this.instance.defaults.baseURL}/auth/token/refresh/`;
            if (SDK_LOG_ENABLED) console.log('[Token Refresh] Refreshing at:', refreshUrl);
            const response = await axios.post<{ access: string; refresh?: string }>(
              refreshUrl,
              { refresh: refreshToken }
            );
            const data = response.data;
            if (SDK_LOG_ENABLED) {
              console.log('[Token Refresh] Response received:', data);
              console.log('[Token Refresh] Access token in response:', data.access ? data.access.substring(0, 50) + '...' : 'null');
              console.log('[Token Refresh] Refresh token in response:', data.refresh ? data.refresh.substring(0, 50) + '...' : 'null');
            }
            
            const { access, refresh } = data;
            if (access && refresh) {
              if (SDK_LOG_ENABLED) console.log('[Token Refresh] Updating both tokens');
              TokenManager.setTokens(access, refresh);
            } else if (access) {
              if (SDK_LOG_ENABLED) console.log('[Token Refresh] Updating only access token');
              TokenManager.setAccessToken(access);
            } else {
              throw new Error('No access token in refresh response');
            }
            
            const storedToken = TokenManager.getAccessToken();
            if (SDK_LOG_ENABLED) {
              console.log('[Token Refresh] Token stored in localStorage:', storedToken ? storedToken.substring(0, 50) + '...' : 'null');
              console.log('[Token Refresh] Tokens match:', storedToken === access);
            }
            
            this.processQueue(null, null); // 唤醒队列
            
            // 关键修复：删除旧的认证头，让请求拦截器重新设置
            this.removeAuthorizationHeader(originalRequest.headers as unknown)
            if (SDK_LOG_ENABLED) console.log('[Token Refresh] Retrying original request to:', originalRequest.url);
            return this.instance(originalRequest);

          } catch (refreshError) {
            this.processQueue(refreshError, null);
            TokenManager.clearTokens();
            
            const event = new CustomEvent('auth-failure');
            window.dispatchEvent(event);

            // refresh 失败：回传规范化错误，避免把技术错误直接暴露给用户
            return Promise.reject(this.formatError(error));
          } finally {
            this.isRefreshing = false;
          }
        }

        if (this.sdkLogEnabled && Math.random() < this.sdkLogSample) {
          try {
            const cfg = toRecord(error.config as unknown)
            const metadata = toRecord(cfg.metadata)
            const startTime = metadata.startTime
            const startMs = startTime instanceof Date ? startTime.getTime() : Date.now()
            const ms = Date.now() - startMs
            const method = typeof cfg.method === 'string' ? cfg.method.toUpperCase() : undefined
            const url = typeof cfg.url === 'string' ? cfg.url : undefined
            console.warn('[SDK][HTTP][err]', error.response?.status, method, url, `${ms}ms`, error.response?.data ?? error.message)
          } catch {}
        }
        return Promise.reject(this.formatError(error));
      }
    )
  }

  private removeAuthorizationHeader(headers: unknown): void {
    if (!headers) return

    // axios v1 AxiosHeaders 实例：支持 delete('Authorization')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const del = (headers as any).delete
    if (typeof del === 'function') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      try { Reflect.apply(del as any, headers, ['Authorization']) } catch {}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      try { Reflect.apply(del as any, headers, ['authorization']) } catch {}
      return
    }

    // 兼容普通对象 headers
    if (isRecord(headers)) {
      delete headers.Authorization
      delete headers.authorization
    }
  }

  private removeContentTypeHeader(headers: unknown): void {
    if (!headers) return

    // axios v1 AxiosHeaders 实例：支持 delete('Content-Type')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const del = (headers as any).delete
    if (typeof del === 'function') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      try { Reflect.apply(del as any, headers, ['Content-Type']) } catch {}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      try { Reflect.apply(del as any, headers, ['content-type']) } catch {}
      return
    }

    // 兼容普通对象 headers
    if (isRecord(headers)) {
      delete headers['Content-Type']
      delete headers['content-type']
    }
  }

  private processQueue(error: unknown, token: string | null): void {
    this.failedQueue.forEach(prom => {
      if (error) prom.reject(error)
      else if (token) prom.resolve(token)
    })
    this.failedQueue = []
  }

  private extractPayload<T>(payload: unknown): T {
    if (payload && typeof payload === 'object' && Object.prototype.hasOwnProperty.call(payload, 'data')) {
      return (payload as { data: T }).data
    }
    return payload as T
  }

  private formatError(error: AxiosError): ErrorResponse {
    if (error.response?.data) {
      const data = toRecord(error.response.data as unknown);
      const rawErrors = data.errors
      let errors: ErrorResponse['errors'] | undefined
      if (isRecord(rawErrors)) {
        const out: Record<string, string[]> = {}
        for (const [k, v] of Object.entries(rawErrors)) {
          if (Array.isArray(v)) out[k] = v.map(String)
        }
        errors = out
      }
      return {
        error: (data.error_code as ApiErrorCode) || (data.error as ApiErrorCode) || ApiErrorCode.ApiError,
        message: (typeof data.message === 'string' ? data.message : (typeof data.detail === 'string' ? data.detail : '请求失败')),
        status: 'error',
        detail: typeof data.detail === 'string' ? data.detail : undefined,
        errors,
      };
    }
    return {
      error: ApiErrorCode.NetworkError,
      message: '网络错误或后端服务未响应',
      status: 'error',
    };
  }

  async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.get<ApiResponse<T>>(url, config)
    return this.extractPayload<T>(response.data)
  }

  async post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.post<ApiResponse<T>>(url, data, config)
    return this.extractPayload<T>(response.data)
  }
  
  async put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.put<ApiResponse<T>>(url, data, config)
    return this.extractPayload<T>(response.data)
  }

  async patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.patch<ApiResponse<T>>(url, data, config)
    return this.extractPayload<T>(response.data)
  }

  async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.delete<ApiResponse<T>>(url, config)
    return this.extractPayload<T>(response.data)
  }

  async upload<T = unknown>(
    url: string,
    file: File,
    onUploadProgress?: AxiosRequestConfig['onUploadProgress']
  ): Promise<T> {
    const formData = new FormData()
    formData.append('file', file)
    // ⚠️ 不要手动设置 Content-Type（浏览器需要 boundary）；交给 Axios 自动处理 multipart/form-data
    const response = await this.instance.post<ApiResponse<T>>(url, formData, { onUploadProgress })
    return response.data.data as T
  }

  getAxiosInstance(): AxiosInstance {
    return this.instance
  }
}

export const httpClient = new HttpClient()
export { TokenManager, HttpClient }

declare module 'axios' {
  interface AxiosRequestConfig {
    metadata?: {
      startTime: Date
    }
    /** 自定义 meta：前端拦截器消费，不会发送到后端 */
    meta?: {
      /** 是否在白名单命中时注入 period 参数 */
      attachPeriod?: boolean
    }
  }
}
